<template>
  <div
    class="card px-4 py-3"
    :data-min-from-last-mandatory="step.period || 0"
    :data-is-mandatory="step.is_mandatory || 0"
  >
    <header class="card-header flex justify-between items-center">
      <div class="flex space-x-3 items-center">
        <div
          class="rounded-full font-bold text-xs px-2.5 text-muted-800 h-6 items-center flex"
          :class="[
            step.is_mandatory ? 'bg-primary-yellow' : step.show_details ? 'bg-info !text-white' : 'bg-muted-200'
          ]"
        >
          Day {{ step.sum_days || 0 }}
        </div>
        <span
          class="text-primary-500 dark:text-primary-400"
          :class="{ 'italic': !step.choice_name }"
        >
          {{ stepName ?? 'Please add a name to this step' }}
        </span>
      </div>

      <!-- Card Open / Close Action -->
      <div
        class="cursor-pointer text-primary-500 dark:text-primary-400 hover:text-primary-400 dark:hover:text-primary-500"
        @click="toggleCard()"
      >
        {{ step.show_details ? 'Close' : 'Edit' }}

        <i
          class="fas fa-fw"
          :class="[step.show_details ? 'fa-caret-up' : 'fa-caret-down']"
        />
      </div>
    </header>

    <!-- Card Content Start -->
    <transition name="slide-fade">
      <div
        v-if="step.show_details"
        class="mt-3"
      >
        <form
          @submit.prevent
          @change="onChange"
        >
          <input
            type="hidden"
            name="is_mandatory[]"
            :value="step.is_mandatory || 0"
          >

          <!-- Select Step Field -->

          <core-select
            v-model="choice_name"
            name="choice_name[]"
            validation
            :errors="v$.choice_name.$errors"
            label="Select step"
            hint="Required field"
          >
            <option
              :value="undefined"
              disabled
              hidden
            >
              Choose a type...
            </option>

            <option
              v-for="(opt, idx) in workflow.default_possible_choices_name_list"
              :key="idx"
              :value="idx"
            >
              {{ opt.name }}
            </option>
          </core-select>

          <!-- Notification Type Field -->

          <core-select
            v-if="!isAdsMaterial"
            v-model="choice"
            name="choice[]"
            validation
            :errors="v$.choice.$errors"
            label="Notification type"
            hint="Required field"
          >
            <option
              :value="undefined"
              disabled
              hidden
            >
              Choose a type...
            </option>

            <option
              v-for="(opt, idx) in getDefaultPossibleChoiceList"
              :key="idx"
              :value="idx"
            >
              {{ opt.name }}
            </option>
          </core-select>

          <!-- Letter Type Field -->

          <core-select
            v-if="![NotificationType.NotificationNone, NotificationType.AdsNone].includes(+choice) && !isAdsMaterial"
            v-model="letter_type"
            name="letter_type[]"
            validation
            :errors="v$.letter_type.$errors"
            label="Letter Type"
            hint="Required field"
          >
            <template #label>
              Letter Type

              <span
                class="tooltip has-tooltip-right has-tooltip-arrow no-underline"
                data-tooltip="Required field"
              >
                <i class="fas fa-info-circle text-info" />
              </span>
            </template>

            <option
              :value="undefined"
              disabled
              hidden
            >
              Choose a type...
            </option>

            <option
              v-for="(opt, idx) in workflow.default_possible_letter_type_list"
              :key="idx"
              :value="opt"
            >
              {{ opt }}
            </option>
          </core-select>

          <!-- Alternate Letter Type Field -->

          <core-select
            v-if="![NotificationType.NotificationNone, NotificationType.AdsNone].includes(+choice) && !isAdsMaterial"
            v-model="alt_letter_type"
            name="alt_letter_type[]"
            validation
            :errors="v$.alt_letter_type.$errors"
            label="Alternate Letter Type"
            hint="Required field"
          >
            <option
              :value="undefined"
              disabled
              hidden
            >
              Choose a type...
            </option>

            <option
              v-for="(opt, idx) in workflow.default_possible_alt_letter_type_list"
              :key="idx"
              :value="opt"
            >
              {{ opt }}
            </option>
          </core-select>

          <!-- Content Sent Field -->

          <core-select
            v-if="!isAdsMaterial"
            v-model="template_name"
            name="template_name[]"
            validation
            :errors="v$.template_name.$errors"
            label="Content sent"
            hint="Required field"
          >
            <option
              :value="undefined"
              disabled
              hidden
            >
              Choose a template ...
            </option>

            <option
              v-for="(opt, idx) in templatesList"
              :key="idx"
              :value="idx"
            >
              {{ opt }}
            </option>
          </core-select>

          <!-- Number of Days before Auction Filed -->

          <core-input
            v-model="current_period"
            name="period[]"
            inputmode="numeric"
            pattern="[0-9]*"
            placeholder="0"
            validation
            :errors="v$.current_period.$errors"
            @input="updatePeriod"
          >
            <template #label>
              {{
                backwards == 1 || [5, 6, 9].includes(step.choice_name) ?
                  'Number of Days before Auction' :
                  'Number of day(s) from previous step'
              }}

              <span
                class="tooltip has-tooltip-right has-tooltip-arrow no-underline"
                :data-tooltip="'Required field' + '\n' + 'Only digits are allowed' + '\n' + 'Minimum value should be taken from the state'"
              >
                <i class="fas fa-info-circle text-info" />
              </span>
            </template>
          </core-input>

          <!-- Actions Footer -->

          <div class="flex justify-between items-center">
            <toggle
              v-if="[NotificationType.Email, NotificationType.USPS, NotificationType.SMS].includes(+choice) && !isAdsMaterial"
              v-model="is_alternate_required"
              label="Alternate Required"
            />

            <div class="space-x-3">
              <button
                v-if="!step.is_mandatory"
                class="button is-white"
                @click.prevent="deleteElement"
              >
                Remove
              </button>
              <button
                class="button primary"
                @click="validateForm"
              >
                Validate
              </button>
            </div>
          </div>
        </form>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

/**
 * Enum Interfaces Start
 * Hardcoded for now, should be moved to the abstraction
 * and be provided from the backend in the other format.
 * @todo
 */

const WorkflowStep = {
  LienLetterNotice1: 1,
  LienLetterNotice2: 2,
  LienToSaleLetter1: 3,
  LienToSaleLetter2: 4,
  Newspaper1: 5,
  Newspaper2: 6,
  Notification: 7,
  UCCSearch: 8,
  AuctionReminder: 9,
  LateNotice: 10
};

const WorkflowStepLiteral = {
  1: 'lien_notice',
  2: 'lien_notice2',
  3: 'lien_sale',
  4: 'lien_sale2',
  5: 'newspaper1',
  6: 'newspaper2'
};

const NotificationType = {
  SMS: 1,
  Email: 2,
  USPS: 3,
  NotificationNone: 4,
  NewspaperPublication: 5,
  Verification: 6,
  Notification: 7,
  AdsNone: 8
};
/**
 * Enum Interfaces End ..
 */

export default {
  name: 'EditStepWorkflow',
  components: {
    toggle: () => import('@/components/core/Toggle'),
    'core-input': () => import('@/components/core/Input'),
    'core-select': () => import('@/components/core/Select')
  },
  props: {
    step: { type: Object, default: () => ({}) },
    index: { type: Number, default: 0 },
    workflow: { type: Object, default: () => ({}) }
  },
  setup: () => ({ v$: useVuelidate(), NotificationType, WorkflowStep }),
  data: () => ({
    // Model Start
    choice_name: '',
    choice: '',
    letter_type: '',
    alt_letter_type: '',
    template_name: '',
    current_period: 0,
    is_alternate_required: false
    // Model End
  }),
  validations() {
    return {
      choice_name: { required },
      choice: { required },
      letter_type: { requiredIf: !this.isAdsMaterial },
      alt_letter_type: { requiredIf: !this.isAdsMaterial },
      template_name: { requiredIf: !this.isAdsMaterial },
      current_period: {
        required,
        onlyDigits: helpers.withMessage('Only digits are allowed', helpers.regex(/^[0-9]*$/)),
        $autoDirty: true,
        min: helpers.withParams(
          {
            type: 'min',
            min: this.companyState[WorkflowStepLiteral[+this.choice_name]] || 0
          },
          helpers.withMessage(
            ({ $params }) => `Minimum value should be taken from the state (${$params.min})`,
            (value) => (this.companyState[WorkflowStepLiteral[+this.choice_name]] || 0) <= +value
          )
        )
      }
    };
  },
  computed: {
    ...mapGetters({
      smsTemplates: 'workflow/getSMSTemplates'
    }),
    isAdsMaterial() {
      return [WorkflowStep.Newspaper1, WorkflowStep.Newspaper2, WorkflowStep.UCCSearch].includes(+this.choice_name);
    },
    companyState() {
      return this.$store.state.company.state;
    },
    backwards() {
      // @todo
      const { possible_name_list: possibleNameList } = this.step;
      return possibleNameList && possibleNameList[this.choice_name]
        && possibleNameList[this.choice_name]?.date_backwards;
    },
    stepName() {
      const {
        choice,
        choice_name: choiceName,
        possible_choices_list: possibleChoicesList,
        possible_name_list: possibleNameList
      } = this.step;

      const {
        default_possible_choices_name_list: defaultPossibleChoicesNameList,
        default_possible_choices_list: defaultPossibleChoicesList
      } = this.workflow;

      const nameList = possibleNameList || defaultPossibleChoicesNameList;
      const choiceList = possibleChoicesList || defaultPossibleChoicesList;
      return [choiceList[choice]?.name, nameList[choiceName]?.name].filter((it) => it).join(': ');
    },

    getDefaultPossibleChoiceList() {
      const { default_possible_choices_list: defaultPossibleChoicesList } = this.workflow;

      if (!this.choice_name) {
        return defaultPossibleChoicesList;
      }

      switch (+this.choice_name) {
        case WorkflowStep.Newspaper1: // Newspaper 1
        case WorkflowStep.Newspaper2: // Newspaper 2
        case WorkflowStep.UCCSearch: // UCC Search
          return {
            // All Ads types
            ...Object.fromEntries(
              Object.entries(defaultPossibleChoicesList).filter(([, value]) => value.category !== 'notification')
            )
          };

        default:
          return {
            // All notification types + None
            ...Object.fromEntries(
              Object.entries(defaultPossibleChoicesList).filter(([, value]) => value.category === 'notification')
            ),
            4: {
              category: 'notification',
              name: 'None'
            }
          };
      }
    },

    /**
     * Returns a list of templates for the current step
     * and notification type.
     *
     * @returns {Array} An array of templates.
     */
    templatesList() {
      /**
       * If the current Notification Type is EMAIL or USPS,
       * allow the selection of any of the email templates.
       *
       * Otherwise, if the current Notification Type is SMS,
       * allow the selection of any of the SMS templates.
       *
       * Otherwise, return None.
       */

      switch (+this.choice) {
        case NotificationType.USPS:
        case NotificationType.Email:
          /**
           * If the current step is Notification (ID: 7),
           * (???) then the template list should be taken with `content_sms` key.
           *
           * Otherwise, the template list should be shown full.
           * @todo clarify this logic
           */
          return +this.choice_name === WorkflowStep.Notification
            ? { content_sms: this.workflow.default_possible_template_list.content_sms }
            : this.workflow.default_possible_template_list;

        case NotificationType.SMS:
          return this.smsTemplates;

        default:
          return {
            content_ad: 'None'
          };
      }
    }
  },
  watch: {
    index: {
      handler() {
        this.resetModel();
      },
      immediate: true
    },
    /**
     * Updates the Notification Type for the current step.
     * @param {string} val - The new current step.
     */
    choice_name() {
      this.is_alternate_required = this.isAdsMaterial ? false : this.step.is_alternate_required || true;

      // if (!prevChoiceName) return; // On the first load, the previous step is undefined.

      /**
       * If the current step is Newspaper 1 (ID: 5), Newspaper 2 (ID: 6) or UCC Search (ID: 8),
       * then the Notification Type should be None by default.
       *
       * Otherwise, the first Notification Type should be selected by default.
       */

      switch (+this.choice_name) {
        case WorkflowStep.UCCSearch:
          this.choice = NotificationType.AdsNone;
          break;

        case WorkflowStep.Newspaper1:
        case WorkflowStep.Newspaper2:
          this.choice = NotificationType.NewspaperPublication;
          break;

        default:
          // eslint-disable-next-line prefer-destructuring
          this.choice = Object.keys(this.getDefaultPossibleChoiceList)[0];
          break;
      }
    },
    choice(val, prevChoice) {
      if (!prevChoice) return; // On the first load, the previous step is undefined.

      /**
       * If the current step is Newspaper 1 (ID: 5), Newspaper 2 (ID: 6) or UCC Search (ID: 8),
       * then the Template Content should be unset.
       *
       * Otherwise, the first Template Content should be selected by default.
       */
      this.template_name = this.isAdsMaterial
        ? ''
        : Object.keys(this.templatesList)[0];

      if ([NotificationType.NotificationNone, NotificationType.AdsNone].includes(+val)) {
        this.letter_type = '';
        this.alt_letter_type = '';
        this.is_alternate_required = false;
      }

      if (this.isAdsMaterial) {
        this.is_alternate_required = false;
      }
    }
  },
  mounted() {
    this.resetModel();
  },
  methods: {
    toggleCard() {
      this.$emit('toggle');
    },
    updatePeriod() {
      if (this.v$.current_period.$invalid) return;

      this.$emit('computePeriods', this.current_period);
    },
    async validateForm() {
      const valid = await this.v$.$validate();
      if (!valid) return;

      this.$emit('validate-step', {
        ...this.step,
        choice_name: this.choice_name,
        choice: this.choice,
        letter_type: this.letter_type,
        alt_letter_type: this.alt_letter_type,
        template_name: this.template_name,
        current_period: this.current_period,
        is_alternate_required: this.is_alternate_required
      });
    },
    onChange() {
      this.$emit('change', {
        ...this.step,
        choice_name: this.choice_name,
        choice: this.choice,
        letter_type: this.letter_type,
        alt_letter_type: this.alt_letter_type,
        template_name: this.template_name,
        current_period: this.current_period,
        is_alternate_required: this.is_alternate_required
      });
    },
    deleteElement() {
      this.$emit('delete-step');
    },
    resetModel() {
      this.choice_name = this.step.choice_name || '';
      this.choice = this.step.choice;
      this.letter_type = this.step.letter_type || '';
      this.alt_letter_type = this.step.alt_letter_type || '';
      this.template_name = this.step.template_name || '';
      this.current_period = this.step.current_period || 0;
      this.is_alternate_required = this.isAdsMaterial ? false : this.step.is_alternate_required || false;
    }
  }
};
</script>

<style scoped src='../../../../public/assets/css/bulma-extension/bulma-tooltip.min.css'></style>
<style lang="scss" scoped>
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all .3s ease;
}
.slide-fade-leave-active {
  transition: all .1s ease;
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
